import { useForm } from "react-hook-form";

import type { LanguageDto } from "@/api/types";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { Form } from "@/components/Form/Form";
import { FormHiddenInput } from "@/components/Form/FormHiddenInput";
import { FormInput } from "@/components/Form/FormInput";
import { Translate01Icon } from "@/components/Icons/Translate01Icon";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { Button } from "@/design-system/Button";
import { FormField } from "@/design-system/FormField";
import { Notice } from "@/design-system/Notice";
import { getFlagIcon } from "@/helpers/flags";
import { createRequiredStringRule } from "@/helpers/rules";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useSlug } from "@/hooks/useSlug";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

export interface CreateOrEditFolderFormValues {
  name: string;
  parentFolderId: string;
}

export interface DocumentFolderFormProps {
  folderId: string;
  folderName: string;
  onSubmit: (data: CreateOrEditFolderFormValues) => void;
  originalLanguageId?: LanguageDto["id"];
  defaultFormValues: CreateOrEditFolderFormValues;
  isEditMode: boolean;
  isSubmitting: boolean;
}

export function DocumentFolderForm(props: DocumentFolderFormProps): React.ReactNode {
  const { defaultFormValues } = props;
  const { t } = useTranslation();
  const sessionUser = useSessionUser();
  const formMethods = useForm<CreateOrEditFolderFormValues>({
    mode: "onChange",
    defaultValues: defaultFormValues,
  });
  const slug = useSlug();

  const flagIcon = getFlagIcon({
    languageId: props.originalLanguageId ?? sessionUser.uiLanguage.id,
    width: 16,
  });

  const title = props.isEditMode
    ? t("page.document.folder.create-or-edit.edit-mode.title")
    : t("page.document.folder.create-or-edit.create-mode.title");

  return (
    <DocumentPage
      containerBoundary="inherit"
      contentSize="constrained"
      tabTitle={title}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            {
              name: props.folderName,
              to: routes.documents.documentsList({
                slug,
                folderId: props.folderId,
              }),
            },
            {
              name: title,
            },
          ]}
        />
      }
    >
      <Form.Root formMethods={formMethods} onSubmit={props.onSubmit}>
        <Form.Container>
          <Form.Body>
            <Notice
              icon={<Translate01Icon />}
              variant="special"
              message={
                props.isEditMode
                  ? t("common.label.you-are-editing-in-original-content-language")
                  : t("common.label.your-input-will-be-auto-translated")
              }
            />
            <FormField label={t("model.document.folder.name")} actions={flagIcon} required>
              <FormInput<CreateOrEditFolderFormValues>
                data-testid="documents-folder-name"
                name="name"
                rules={{
                  validate: {
                    required: createRequiredStringRule(t, "model.document.folder.name"),
                  },
                  maxLength: {
                    message: t("components.form.error.max-length", {
                      length: NAME_MAX_LENGTH,
                    }),
                    value: NAME_MAX_LENGTH,
                  },
                }}
              />
            </FormField>
            <FormHiddenInput<CreateOrEditFolderFormValues> name="parentFolderId" />
          </Form.Body>
          <Form.Footer
            renderPrimaryAction={() => (
              <Button type="submit" isLoading={props.isSubmitting} data-testid="documents-folder-submit">
                {props.isEditMode ? t("common.action.edit") : t("common.action.create")}
              </Button>
            )}
          />
        </Form.Container>
      </Form.Root>
    </DocumentPage>
  );
}

const NAME_MAX_LENGTH = 40;
